import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MessageCircle, Inbox, Plus, ArrowRight, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import CareRequestsClient from '@/components/CareRequestsClient';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const userRes = await api.get('/auth/me');

      if (userRes.data.role === 'provider' || userRes.data.provider) {
        navigate('/provider/dashboard');
        return;
      }

      setUser(userRes.data);
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#00e7ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8fb]" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 py-8">

        <div className="bg-white rounded-3xl border shadow-sm p-6 md:p-8 mb-5">
          <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6 items-center">
            <div>
              <span className="inline-flex mb-4 px-3 py-1 rounded-full bg-cyan-50 text-[#00a9b8] text-xs font-black uppercase">
                Panel Cliente
              </span>

              <h1 className="text-3xl md:text-4xl font-black text-[#33404f] leading-tight">
                Publica una solicitud y recibe propuestas de residencias
              </h1>

              <p className="text-gray-600 text-base md:text-lg mt-4 max-w-2xl">
                Cuéntanos qué necesita tu familiar y las residencias Premium podrán revisar tu solicitud y enviarte propuestas directamente desde SeniorAdvisor.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={() => {
                    setActiveTab('requests');
                    window.dispatchEvent(new Event('open-care-request-form'));
                  }}
                  className="bg-[#00e7ff] hover:bg-[#00c4d4] text-[#33404f] rounded-xl font-black px-6 py-6"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Crear nueva solicitud
                </Button>

                <Link to="/search">
                  <Button variant="outline" className="w-full sm:w-auto rounded-xl border-[#33404f] text-[#33404f] px-6 py-6 font-bold">
                    <Search className="w-5 h-5 mr-2" />
                    Buscar residencias
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-[#33404f] rounded-3xl p-6 text-white">
              <FileText className="w-10 h-10 mb-4 text-[#00e7ff]" />
              <h3 className="font-black text-xl mb-2">¿Para qué sirve?</h3>
              <p className="text-sm text-white/80">
                Tu solicitud funciona como una publicación privada para residencias Premium. Ellas pueden enviarte una propuesta según comuna, necesidad, urgencia y presupuesto.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-5">
          <button
            type="button"
            onClick={() => setActiveTab('requests')}
            className="relative text-left bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all"
          >
            <Inbox className="w-8 h-8 text-[#00a9b8] mb-4" />
            <h3 className="font-black text-[#33404f]">Mis solicitudes</h3>
            <p className="text-sm text-gray-500 mt-1">Crea y revisa tus solicitudes publicadas.</p>
          </button>

          <Link to="/chat" className="relative text-left bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all">
            <MessageCircle className="w-8 h-8 text-[#00a9b8] mb-4" />
            <h3 className="font-black text-[#33404f]">Mensajes</h3>
            <p className="text-sm text-gray-500 mt-1">Conversa con residencias que te contacten.</p>
            <ArrowRight className="absolute top-5 right-5 w-5 h-5 text-gray-300" />
          </Link>

          <Link to="/search" className="relative text-left bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all">
            <Search className="w-8 h-8 text-[#00a9b8] mb-4" />
            <h3 className="font-black text-[#33404f]">Buscar residencias</h3>
            <p className="text-sm text-gray-500 mt-1">También puedes contactar una residencia directamente.</p>
            <ArrowRight className="absolute top-5 right-5 w-5 h-5 text-gray-300" />
          </Link>
        </div>

        <div className="flex gap-2 mb-5 overflow-x-auto">
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-5 py-3 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
              activeTab === 'requests'
                ? 'bg-[#33404f] text-white'
                : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}
          >
            Mis Solicitudes
          </button>

          <Link
            to="/chat"
            className="px-5 py-3 rounded-xl text-sm font-bold transition-colors whitespace-nowrap bg-white border text-gray-600 hover:bg-gray-50"
          >
            Mensajes
          </Link>
        </div>

        {activeTab === 'requests' && (
          <div className="bg-white rounded-3xl border shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-black text-[#33404f]">
                  Solicitudes para residencias
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Publica lo que necesitas y recibe propuestas de residencias Premium.
                </p>
              </div>
            </div>

            <CareRequestsClient />
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
